'use client'
import { type FC, useState } from 'react'
import { type TableBlock as TableBlockType } from '@/payload-types'
import RichText from '@/ui/components/RichText'
import { HeadingTag, Title } from '@/ui/components/Title'
import { getIdString } from '@/utils/getIdString'
import { Table } from '@/ui/atoms/Table/table'
import { TableRowProps } from '@/ui/atoms/Table/TableRow'
import { Icon } from '@/ui/atoms/Icon'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'

interface ExtendedTableBlockType extends TableBlockType {
  fixedTitleStyle?: HeadingTag
}

export const TableBlock: FC<ExtendedTableBlockType> = ({ title, titleHierarchy, text, tables, fixedTitleStyle }) => {
  const [openStates, setOpenStates] = useState<boolean[]>(Array(tables?.length || 0).fill(true))

  const getTableRows = (tableData: any[]): TableRowProps[] => {
    return tableData.map((row, index) => ({
      label: row.label || '',
      value: row.value,
      valueRichText: row.valueRichText,
      useRichText: row.useRichText,
      className: index % 2 === 0 ? 'bg-bg-base' : 'bg-blue-98',
    }))
  }

  const handleClick = (index: number) => {
    if (openStates[index]) {
      // Log the table block toggle event to Google Analytics
      sendGAEvent('event', 'tableAccordionOpen', { label: tables?.at(index)?.tableHeader || 'Table Block' })
      trackEvent('tableAccordionOpen', { label: tables?.at(index)?.tableHeader || 'Table Block' })
    }
    const newOpenStates = [...openStates]
    newOpenStates[index] = !newOpenStates[index]
    setOpenStates(newOpenStates)
  }

  return (
    <div className="lg:mt-section mt-mobile-section">
      {title && (
        <Title hierarchy={titleHierarchy} fixedStyle={fixedTitleStyle} className="lg:mb-8 mb-5">
          {title}
        </Title>
      )}
      {text && (
        <RichText data={text} className="lg:mb-8 mb-5" enableGutter={false} enableProse={true} />
      )}
      <div className="flex flex-col gap-section">
        {tables?.map((tableItems, index) => (
          <div key={index}>
            {tableItems.tableHeader && (
              <div
                className="bg-bg-base border-border-subtle border rounded-md"
                id={getIdString(tableItems.tableHeader ?? '')}
              >
                <button
                  onClick={() => handleClick(index)}
                  className={`w-full flex justify-between items-center text-body-M-semibold text-text-base bg-bg-base hover:bg-neutral-95 transition-all duration-200 ease-in-out p-card-s rounded-md ${
                    openStates[index] ? 'lg:mb-grid-gutter mb-mobile-grid-gutter' : ''
                  }`}
                >
                  <div className="flex gap-3 items-center">{tableItems.tableHeader}</div>
                  <Icon name={openStates[index] ? 'arrow-up' : 'arrow-down'} className="w-6 h-6" />
                </button>
                {openStates[index] && (
                  <div>
                    <div className="px-card-s"></div>
                    <div className="px-card-s py-2">
                      {tableItems.table && (
                        <Table rows={getTableRows(tableItems.table)} className="w-full" />
                      )}
                    </div>
                    <div className="px-card-s pb-card-s"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={'w-screen h-full top-0 bottom-0 bg-red-20'}></div>
    </div>
  )
}
